import { useState, useEffect, useRef } from "react";
import { Search, X, Loader2, Layers, Library, Grid3x3, Palette, Sparkles } from "lucide-react";
import { searchGlobal } from "../lib/api";
import { BLOC_ORDER } from "../config/blocs";

// Barre de recherche globale : un seul champ pour retrouver un bloc, une série,
// un set, une carte ou un illustrateur, puis atterrir directement au bon endroit.
//
// Les blocs viennent de la config locale (config/blocs.js) ; le reste du backend.
// Les cartes et illustrateurs ne couvrent que les sets déjà parcourus, puisqu'ils
// sont cherchés dans le cache — voir le commentaire de l'endpoint /search.
// `autoFocus` + `onClose` + `wide` : mode « champ déplié » de la barre du haut
// (TopNav, 2026-10-03) — le champ s'ouvre depuis une loupe, prend sa pleine
// largeur, et se referme (onClose) sur Échap, après un choix, ou au clic à
// côté quand il est vide.
export const GlobalSearch = ({ onSelectBloc, onSelectSerie, onSelectSet, onSelectCard, onSelectIllustrator, onSelectSpecies,
  autoFocus = false, onClose, wide = false }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Fermeture au clic à côté.
  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
        if (onClose && !boxRef.current.querySelector("input")?.value) onClose();
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [onClose]);

  // Recherche différée : on attend une pause de frappe pour ne pas envoyer
  // une requête par lettre.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults(null); return; }
    setLoading(true);
    const timer = setTimeout(() => {
      searchGlobal(q)
        .then(data => {
          const rx = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
          const blocs = BLOC_ORDER.filter(b => rx.test(b.label));
          setResults({ ...data, blocs });
        })
        .catch(err => console.error("Recherche :", err))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const choose = (fn, arg) => {
    fn(arg);
    setQuery("");
    setResults(null);
    setOpen(false);
    if (onClose) onClose();
  };

  const groups = results ? [
    // L'espèce passe en TÊTE : taper « Pikachu » doit d'abord proposer « toutes
    // les cartes de Pikachu », pas une carte précise perdue dans une extension.
    { key: "species", label: "Pokémon", icon: Sparkles, items: results.species,
      render: n => n, onPick: n => choose(onSelectSpecies, n) },
    { key: "blocs", label: "Blocs", icon: Layers, items: results.blocs,
      render: b => b.label, onPick: b => choose(onSelectBloc, b) },
    { key: "series", label: "Séries", icon: Layers, items: results.series,
      render: s => s.name, onPick: s => choose(onSelectSerie, s) },
    { key: "sets", label: "Collections", icon: Library, items: results.sets,
      render: s => <>{s.name}<span className="text-zinc-600 ml-2 text-xs">{s.serie_name}</span></>,
      onPick: s => choose(onSelectSet, s.id) },
    { key: "cards", label: "Cartes", icon: Grid3x3, items: results.cards,
      render: c => <>{c.name}<span className="text-zinc-600 ml-2 text-xs">{c.set_name} · {c.localId}</span></>,
      onPick: c => choose(onSelectCard, c.id) },
    { key: "illustrators", label: "Illustrateurs", icon: Palette, items: results.illustrators,
      render: i => i, onPick: i => choose(onSelectIllustrator, i) },
  ].filter(g => (g.items || []).length > 0) : [];

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          autoFocus={autoFocus}
          onKeyDown={(e) => {
            if (e.key === "Escape") { setQuery(""); setOpen(false); if (onClose) onClose(); }
            // Entrée = ouvrir le premier résultat, dans l'ordre affiché.
            // On ignore la frappe tant que la recherche différée n'a pas
            // répondu, pour ne pas ouvrir le résultat de la requête PRÉCÉDENTE.
            if (e.key === "Enter" && !loading) {
              const first = groups[0];
              if (first && first.items.length) { e.preventDefault(); first.onPick(first.items[0]); }
            }
          }}
          placeholder={wide ? "Rechercher une carte, une collection, un illustrateur…" : "Rechercher…"}
          className={`${wide ? "w-full lg:w-96" : "w-28 sm:w-56 lg:w-80"} bg-zinc-900 border border-zinc-800 rounded-lg pl-8 pr-8 py-1.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-zinc-600`}
        />
        {query && (
          <button onClick={() => { setQuery(""); setResults(null); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && query.trim().length >= 2 && (
        <div className="absolute right-0 mt-2 w-[92vw] max-w-[26rem] max-h-[70vh] overflow-y-auto bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl z-50 p-2">
          {loading && !results ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
          ) : !groups.length ? (
            <p className="text-sm text-zinc-500 px-3 py-4 text-center">
              Aucun résultat. Les cartes et illustrateurs ne sont trouvables que
              dans les collections déjà ouvertes au moins une fois.
            </p>
          ) : groups.map(group => {
            const Icon = group.icon;
            return (
              <div key={group.key} className="mb-1.5 last:mb-0">
                <div className="flex items-center gap-1.5 px-2 py-1 text-[0.65rem] font-semibold uppercase tracking-wide text-zinc-600">
                  <Icon className="w-3 h-3" />{group.label}
                </div>
                {group.items.map((item, i) => {
                  // Le tout premier résultat est celui qu'Entrée ouvrira : on
                  // le signale, sinon le raccourci agit à l'aveugle.
                  const isFirst = group.key === groups[0].key && i === 0;
                  return (
                    <button key={i} onClick={() => group.onPick(item)}
                      className={`w-full text-left text-sm rounded px-3 py-1.5 flex items-center justify-between gap-2 ${
                        isFirst ? "bg-zinc-800 text-white" : "text-zinc-200 hover:bg-zinc-800"}`}>
                      <span className="truncate">{group.render(item)}</span>
                      {isFirst && <span className="text-[0.6rem] text-zinc-500 shrink-0">Entrée</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
